/**
 * Cartesian chart builder — covers `line`, `bar`, `area`, `scatter` plus
 * mixed-type (e.g. bar + line) and dual-axis variants.
 *
 * Per ADR-0079 §4.4: `dataset.source = rows`, `dataset.dimensions = dimensions`
 * (no copy), per-series `encode: { x, y }` referring to dimension names so
 * the existing drill-down click contract in `Chart.tsx`
 * (`resolveClickedColumn`) keeps working.
 */

import type { EChartsOption } from "echarts";
import type { CartesianSpec, ValueFormat } from "../echartsSpec.js";
import {
  buildAxisLabelFormatter,
  buildNoDataGraphic,
  buildTooltipFormatter,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type CartesianSeriesType = "line" | "bar" | "area" | "scatter";

type CartesianSeries = {
  name: string;
  type: "line" | "bar" | "scatter";
  // ECharts has no separate "area" type — area = line + areaStyle.
  areaStyle?: Record<string, unknown>;
  encode: { x: string; y: string };
  yAxisIndex?: 0 | 1;
  stack?: string;
};

type LongFormFanout = {
  /** Distinct series values, in order of first occurrence. */
  values: string[];
  /** Index in `dimensions` of the series-discriminator column. */
  seriesIdx: number;
  /** Index in `dimensions` of the value column. */
  valueIdx: number;
};

export function buildCartesianOption(
  spec: CartesianSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  // ── Series construction ──────────────────────────────────────────────────
  const series: CartesianSeries[] = [];
  const datasetSources: Array<Record<string, unknown>> = [
    { source: isEmpty ? [] : rows, dimensions },
  ];

  let needsRightAxis = false;

  if (spec.encoding.y && spec.encoding.y.length > 0) {
    // Wide form: one ECharts series per encoding.y[] entry.
    for (const y of spec.encoding.y) {
      const sType: CartesianSeriesType = y.seriesType ?? defaultSeriesTypeFor(spec.chartType);
      const axisIdx: 0 | 1 = y.axisIndex === 1 ? 1 : 0;
      if (axisIdx === 1) needsRightAxis = true;
      series.push(buildSeries({
        name: y.column,
        x: spec.encoding.x.column,
        y: y.column,
        seriesType: sType,
        axisIndex: axisIdx,
        stack: y.stack,
      }));
    }
  } else if (spec.encoding.series) {
    // Long form: pivot one series per distinct value in series.column.
    const fanout = computeLongFormFanout(spec.encoding.series.column, rows, dimensions);
    if (fanout) {
      const sType = spec.encoding.series.seriesType ?? defaultSeriesTypeFor(spec.chartType);
      const stack = spec.encoding.series.stack;
      // Use ECharts dataset transforms to filter the source per series, so
      // each series operates on its own subset without us copying rows.
      for (let i = 0; i < fanout.values.length; i++) {
        const value = fanout.values[i];
        datasetSources.push({
          fromDatasetIndex: 0,
          transform: {
            type: "filter",
            config: { dimension: spec.encoding.series.column, "=": value },
          },
        });
        series.push({
          name: String(value),
          type: sType === "area" ? "line" : sType,
          ...(sType === "area" ? { areaStyle: {} } : {}),
          encode: {
            x: spec.encoding.x.column,
            y: spec.encoding.series.valueColumn,
          },
          // Index 0 is the source; indices 1..N are the per-series filtered
          // datasets created in this loop.
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          ...({ datasetIndex: i + 1 } as { datasetIndex: number }),
          ...(stack ? { stack } : {}),
        } as CartesianSeries);
      }
    }
  }

  // ── Axes ─────────────────────────────────────────────────────────────────
  const xAxis: Record<string, unknown> = {
    type: spec.encoding.x.dataType,
    ...(spec.encoding.x.format
      ? { axisLabel: { formatter: buildAxisLabelFormatter(spec.encoding.x.format) } }
      : {}),
  };

  const yAxisLeft: Record<string, unknown> = {
    type: "value",
    ...(spec.options.yAxis?.min !== undefined ? { min: spec.options.yAxis.min } : {}),
    ...(spec.options.yAxis?.max !== undefined ? { max: spec.options.yAxis.max } : {}),
    ...(spec.options.yAxis?.format
      ? { axisLabel: { formatter: buildAxisLabelFormatter(spec.options.yAxis.format) } }
      : {}),
  };

  const yAxis: Array<Record<string, unknown>> = [yAxisLeft];
  if (needsRightAxis) {
    yAxis.push({
      type: "value",
      ...(spec.options.yAxisRight?.min !== undefined ? { min: spec.options.yAxisRight.min } : {}),
      ...(spec.options.yAxisRight?.max !== undefined ? { max: spec.options.yAxisRight.max } : {}),
      ...(spec.options.yAxisRight?.format
        ? { axisLabel: { formatter: buildAxisLabelFormatter(spec.options.yAxisRight.format) } }
        : {}),
    });
  }

  // ── Tooltip ──────────────────────────────────────────────────────────────
  // Per-series formats let dual-axis / mixed-unit charts render each series
  // in the tooltip with its own format (e.g. y[0] currency + y[1] number).
  // Falls back to primaryFormat when a series has no explicit format.
  const primaryFormat = pickPrimaryValueFormat(spec);
  const perSeriesFormats: Array<ValueFormat | undefined> | undefined =
    spec.encoding.y && spec.encoding.y.length > 0
      ? spec.encoding.y.map((y) => y.format)
      : undefined;
  const tooltipFormatter = buildTooltipFormatter(
    spec.options.tooltip,
    series as unknown as Array<Record<string, unknown>>,
    primaryFormat,
    perSeriesFormats,
  );
  const tooltip: Record<string, unknown> = {
    show: spec.options.tooltip?.show !== false,
    trigger: "axis",
    ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
  };

  // ── Title / legend / palette ────────────────────────────────────────────
  const palette = resolvePalette(spec.options.color, series.length);

  const option: EChartsOption = {
    ...(spec.options.title ? { title: { text: spec.options.title, ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}) } } : {}),
    ...(palette ? { color: palette } : {}),
    legend: buildLegend(spec.options.legend),
    tooltip,
    dataset: datasetSources,
    xAxis: xAxis as EChartsOption["xAxis"],
    yAxis: yAxis as EChartsOption["yAxis"],
    series: series as unknown as EChartsOption["series"],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };

  return option;
}

function defaultSeriesTypeFor(chartType: CartesianSpec["chartType"]): CartesianSeriesType {
  return chartType;
}

function buildSeries(args: {
  name: string;
  x: string;
  y: string;
  seriesType: CartesianSeriesType;
  axisIndex: 0 | 1;
  stack?: string;
}): CartesianSeries {
  const isArea = args.seriesType === "area";
  const echartsType: "line" | "bar" | "scatter" = isArea
    ? "line"
    : (args.seriesType as "line" | "bar" | "scatter");
  const series: CartesianSeries = {
    name: args.name,
    type: echartsType,
    encode: { x: args.x, y: args.y },
  };
  if (isArea) series.areaStyle = {};
  if (args.axisIndex === 1) series.yAxisIndex = 1;
  if (args.stack) series.stack = args.stack;
  return series;
}

function computeLongFormFanout(
  seriesColumn: string,
  rows: unknown[][],
  dimensions: string[],
): LongFormFanout | null {
  const seriesIdx = resolveColumnIndex(seriesColumn, dimensions);
  if (seriesIdx === null) return null;
  const seen = new Set<string>();
  const values: string[] = [];
  for (const row of rows) {
    const v = row[seriesIdx];
    const key = v == null ? "" : String(v);
    if (!seen.has(key)) {
      seen.add(key);
      values.push(key);
    }
  }
  return { values, seriesIdx, valueIdx: -1 };
}

function pickPrimaryValueFormat(spec: CartesianSpec): ValueFormat | undefined {
  if (spec.options.yAxis?.format) return spec.options.yAxis.format;
  if (spec.encoding.y && spec.encoding.y[0]?.format) return spec.encoding.y[0].format;
  return undefined;
}

function buildLegend(
  legend: CartesianSpec["options"]["legend"],
): Record<string, unknown> {
  const show = legend?.show !== false;
  const base: Record<string, unknown> = { show };
  switch (legend?.position) {
    case "top":
      base.top = 0;
      base.left = "center";
      break;
    case "bottom":
      base.bottom = 0;
      base.left = "center";
      break;
    case "left":
      base.left = 0;
      base.top = "middle";
      base.orient = "vertical";
      break;
    case "right":
      base.right = 0;
      base.top = "middle";
      base.orient = "vertical";
      break;
  }
  return base;
}
